import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Alert,
  Box,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDateTime, formatRelative, summarizeUserAgent } from '../../utils/formatUtils';
import { PageHeader, ClientChip, UserStatusChip, useConfirm, useNotify } from '../../components/common';

const REFRESH_MS = 30000;

const OnlineUsers = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const [data, setData] = useState({ sessions: [], agents: [] });
  const [error, setError] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/online');
      setData(res.data);
      setUpdatedAt(new Date());
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    load();
    if (!autoRefresh) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, autoRefresh]);

  const sessions = useMemo(
    () => data.sessions.filter((s) => clientFilter === 'all' || s.client === clientFilter),
    [data.sessions, clientFilter]
  );
  const uniqueUsers = new Set(data.sessions.map((s) => s.userId)).size;
  const goUser = (id) => navigate(`/admin/users/${encodeURIComponent(id)}`);

  const killSession = (s) =>
    ask({
      title: '세션 강제 종료',
      message: `${s.username || s.email} 회원의 ${s.client} 세션을 종료합니다.\n해당 기기는 다시 로그인해야 합니다.`,
      confirmText: '종료',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/admin/sessions/${s.sessionId}`, { data: { userId: s.userId } });
          notify(res.data.message);
          load();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  return (
    <>
      <PageHeader
        title="현재 접속"
        description={`로그인 세션 ${data.sessions.length}개 (회원 ${uniqueUsers}명) · 접속 중 에이전트 ${data.agents.length}대 · 마지막 갱신 ${
          updatedAt ? formatKstDateTime(updatedAt) : '-'
        }`}
        actions={
          <>
            <FormControlLabel
              control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
              label="30초 자동 갱신"
            />
            <Button startIcon={<RefreshIcon />} onClick={load}>
              새로고침
            </Button>
          </>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            로그인 세션
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={clientFilter} onChange={(_e, v) => v && setClientFilter(v)}>
            <ToggleButton value="all">전체</ToggleButton>
            <ToggleButton value="web">웹</ToggleButton>
            <ToggleButton value="agent">에이전트</ToggleButton>
            <ToggleButton value="admin">관리자</ToggleButton>
            <ToggleButton value="legacy">기존서버</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>회원</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>구분</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>기기</TableCell>
              <TableCell>로그인</TableCell>
              <TableCell>마지막 활동</TableCell>
              <TableCell>만료</TableCell>
              <TableCell align="right">종료</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  접속 중인 세션이 없습니다.
                </TableCell>
              </TableRow>
            )}
            {sessions.map((s) => (
              <TableRow key={s.sessionId} hover>
                <TableCell sx={{ cursor: 'pointer' }} onClick={() => goUser(s.userId)}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {s.username || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.email} · #{s.userId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <UserStatusChip status={s.userStatus} />
                </TableCell>
                <TableCell>
                  <ClientChip client={s.client} />
                </TableCell>
                <TableCell>{s.ipAddress}</TableCell>
                <TableCell>
                  <Tooltip title={s.userAgent || ''}>
                    <span>{summarizeUserAgent(s.userAgent)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatKstDateTime(s.loginTime, { seconds: false })}</TableCell>
                <TableCell>{formatRelative(s.lastActivityAt || s.loginTime)}</TableCell>
                <TableCell>{s.expiresAt ? formatRelative(s.expiresAt) : '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="강제 로그아웃">
                    <IconButton size="small" color="error" onClick={() => killSession(s)}>
                      <LogoutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          접속 중 에이전트 (최근 {data.agentOnlineMinutes ?? '-'}분 내 동기화)
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>회원</TableCell>
              <TableCell>PC 이름</TableCell>
              <TableCell>버전</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>마지막 동기화</TableCell>
              <TableCell>등록일</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.agents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  접속 중인 에이전트가 없습니다.
                </TableCell>
              </TableRow>
            )}
            {data.agents.map((a) => (
              <TableRow key={a.agentId} hover sx={{ cursor: 'pointer' }} onClick={() => goUser(a.userId)}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {a.username || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.email} · #{a.userId}
                  </Typography>
                </TableCell>
                <TableCell>{a.name || '-'}</TableCell>
                <TableCell>{a.version || '-'}</TableCell>
                <TableCell>{a.lastIp || '-'}</TableCell>
                <TableCell>{formatRelative(a.lastSeenAt)}</TableCell>
                <TableCell>{formatKstDateTime(a.createdAt, { seconds: false })}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {confirmDialog}
    </>
  );
};

export default OnlineUsers;
